import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Toast } from "primereact/toast";
import {
  FaArrowLeft,
  FaPhoneAlt,
  FaWhatsapp,
  FaEnvelope,
  FaCopy,
  FaBan,
  FaUnlock,
  FaTrash,
  FaUserTie,
  FaUserMinus,
  FaCoins,
  FaWallet,
  FaAd,
  FaUsers,
  FaVideo,
  FaFlag,
  FaIdCard,
  FaMapMarkerAlt,
  FaMobileAlt,
  FaCalendarAlt,
  FaArrowDown,
  FaArrowUp,
  FaExternalLinkAlt,
  FaYoutube,
  FaFacebookSquare,
  FaInstagram,
  FaCrown,
  FaUserFriends,
  FaUserCheck,
  FaHourglassHalf,
  FaExclamationTriangle,
  FaSyncAlt,
  FaCheckCircle,
  FaUserSlash,
  FaEye,
} from "react-icons/fa";
import { useBaseUrl } from "../Contexts/BaseUrlContext";
import { useStateContext } from "../Contexts/ContextProvider";

/* ================= styles ================= */

const CSS = `
@keyframes upFadeUp { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: none; } }
@keyframes upFade { from { opacity: 0; } to { opacity: 1; } }
@keyframes upZoom { from { opacity: 0; transform: scale(.95); } to { opacity: 1; transform: none; } }
@keyframes upBar { from { width: 0; } }
.up-in { animation: upFadeUp .5s cubic-bezier(.2,.7,.2,1) both; }
.up-fade { animation: upFade .2s ease both; }
.up-zoom { animation: upZoom .25s cubic-bezier(.2,.8,.2,1) both; }
.up-bar { animation: upBar .9s cubic-bezier(.2,.7,.2,1) both; }
.up-press { transition: transform .15s ease, background-color .2s ease, border-color .2s ease, color .2s ease; }
.up-press:hover { transform: translateY(-1px); }
.up-press:active { transform: scale(.96); }
.up-tile { transition: transform .2s ease, box-shadow .2s ease; }
.up-tile:hover { transform: translateY(-3px); box-shadow: 0 16px 30px -18px rgba(15,23,42,.4); }
@media (prefers-reduced-motion: reduce) {
  .up-in, .up-fade, .up-zoom, .up-bar { animation: none; }
  .up-press:hover, .up-press:active, .up-tile:hover { transform: none; }
}
`;

const CARD = "bg-white dark:bg-secondary-dark-bg rounded-2xl border border-slate-200 dark:border-slate-700";

const VIDEO_TYPES = [
  { type: "Youtube Video", icon: FaYoutube, cls: "text-red-600" },
  { type: "Youtube Short", icon: FaYoutube, cls: "text-red-500" },
  { type: "Instagram Reel", icon: FaInstagram, cls: "text-pink-600" },
  { type: "Facebook Video", icon: FaFacebookSquare, cls: "text-blue-600" },
  { type: "Facebook Reel", icon: FaFacebookSquare, cls: "text-blue-500" },
];

/* ================= helpers ================= */

const num = (v) => Number(v || 0);
const money = (n) => `₹${num(n).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
const nm = (s) => (s !== null && s !== undefined && String(s).trim() && String(s).trim() !== "0000-00-00" ? String(s).trim() : null);
const parseAny = (s) => {
  if (!s) return null;
  const d = new Date(String(s).replace(" ", "T"));
  return Number.isNaN(d.getTime()) ? null : d;
};
const fmtDate = (d) => (d ? d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "-");
const fmtTime = (d) => (d ? d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }) : "");
const ageOf = (dob) => {
  const d = parseAny(dob);
  if (!d) return null;
  const a = Math.floor((Date.now() - d.getTime()) / (365.25 * 86400000));
  return a > 0 && a < 120 ? a : null;
};
const waLink = (p) => {
  const d = String(p || "").replace(/\D/g, "").slice(-10);
  return d.length === 10 ? `https://wa.me/91${d}` : null;
};
const ytId = (url) => {
  const m = String(url || "").match(/(?:youtube\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/|live\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
  return m ? m[1] : null;
};
const arr = (d, keys = []) => {
  if (Array.isArray(d)) return d;
  if (!d || typeof d !== "object") return [];
  for (const k of keys) if (Array.isArray(d[k])) return d[k];
  return [];
};
const same = (a, b) => a !== undefined && a !== null && String(a) === String(b);

/* ================= page ================= */

const UserProfile = () => {
  const { user_id } = useParams();
  const navigate = useNavigate();
  const { baseUrl } = useBaseUrl();
  const { currentMode } = useStateContext();
  const toast = useRef(null);

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [extra, setExtra] = useState({});
  const [extraLoading, setExtraLoading] = useState(true);
  const [tab, setTab] = useState("overview");
  const [videoType, setVideoType] = useState("all");
  const [confirm, setConfirm] = useState(null);
  const [deleteText, setDeleteText] = useState("");
  const [working, setWorking] = useState(false);
  const [imgErr, setImgErr] = useState(false);

  /* ----- API (same actions the other pages use) ----- */

  const post = useCallback(
    async (file, action, params = {}) => {
      const fd = new FormData();
      fd.append("action", action);
      Object.entries(params).forEach(([k, v]) => fd.append(k, v));
      const res = await fetch(`${baseUrl}/${file}`, { method: "POST", body: fd, credentials: "include" });
      return res.json();
    },
    [baseUrl]
  );

  const fetchUser = useCallback(async () => {
    setLoading(true);
    try {
      const d = await post("users.php", "user-data", { user_id });
      setUser(d && d.user_id ? d : { notFound: true });
    } catch (e) {
      setUser({ error: true });
    }
    setLoading(false);
  }, [post, user_id]);

  const fetchExtra = useCallback(async () => {
    setExtraLoading(true);
    const jobs = {
      allUsers: post("users.php", "all-users"),
      admins: post("users.php", "all-admin"),
      adminReq: post("users.php", "admin-request"),
      reports: post("users.php", "reported-users", { reported: "0" }),
      wallet: post("wallet.php", "getWalletData", { user_id }),
      ads: post("wallet.php", "getAdsWalletData", { user_id }),
      wdPending: post("wallet.php", "withdrawRequestList", { status: "pending" }),
      wdPaid: post("wallet.php", "withdrawRequestList", { status: "accept" }),
      members: post("group.php", "getGroupMembers"),
      joinReq: post("group.php", "groupJoiningRequestList"),
      customers: post("customer.php", "customerList"),
      userMsgRep: post("group_report.php", "getUserMessageReports"),
      groupMsgRep: post("group_report.php", "getGroupMessageReports"),
      ...Object.fromEntries(VIDEO_TYPES.map((v) => [`v_${v.type}`, post("videos.php", "videos-list", { type: v.type })])),
    };
    const keys = Object.keys(jobs);
    const results = await Promise.allSettled(Object.values(jobs));
    const out = {};
    results.forEach((r, i) => (out[keys[i]] = r.status === "fulfilled" ? r.value : null));
    setExtra(out);
    setExtraLoading(false);
  }, [post, user_id]);

  useEffect(() => {
    setImgErr(false);
    setTab("overview");
    fetchUser();
    fetchExtra();
  }, [fetchUser, fetchExtra]);

  /* ----- derived ----- */

  const d = useMemo(() => {
    const uid = String(user_id);
    const me = arr(extra.allUsers).find((u) => same(u.user_id, uid));
    const isAdmin = arr(extra.admins).some((a) => same(a.user_id, uid));
    const adminReq = arr(extra.adminReq).some((a) => same(a.user_id, uid));
    const reports = arr(extra.reports);
    const reportsAgainst = reports.filter((r) => same(r.user_id, uid));
    const reportsBy = reports.filter((r) => same(r.report_by, uid));
    const umr = arr(extra.userMsgRep, ["reports"]);
    const gmr = arr(extra.groupMsgRep, ["reports"]);
    const msgReportsAgainst = [...umr, ...gmr].filter((r) => same(r.sender_id, uid));
    const walletData = extra.wallet?.status ? extra.wallet.data : null;
    const adsData = extra.ads?.status ? extra.ads.data : null;
    const wdPending = arr(extra.wdPending, ["withdrawals"]).filter((w) => same(w.user_id, uid));
    const wdPaid = arr(extra.wdPaid, ["withdrawals"]).filter((w) => same(w.user_id, uid));
    const members = arr(extra.members, ["members"]);
    const memberships = members.filter((m) => same(m.member_id, uid));
    const referred = Array.from(new Map(members.filter((m) => same(m.joined_by, uid) && !same(m.member_id, uid)).map((m) => [m.member_id, m])).values());
    const ownGroups = memberships.filter((m) => String(m.role).toLowerCase() === "admin" && Number(m.group_id) > 2);
    const joinReq = arr(extra.joinReq, ["requests"]).filter((j) => same(j.user_id, uid));
    const customers = arr(extra.customers, ["customers"]).filter((c) => same(c.user_id, uid));
    const videos = VIDEO_TYPES.flatMap((v) => arr(extra[`v_${v.type}`]).filter((x) => same(x.user_id, uid)).map((x) => ({ ...x, link_type: x.link_type || v.type })));
    return { me, isAdmin, adminReq, reportsAgainst, reportsBy, msgReportsAgainst, walletData, adsData, wdPending, wdPaid, memberships, referred, ownGroups, joinReq, customers, videos };
  }, [extra, user_id]);

  const personal = user
    ? [
        { label: "Email", value: user.email_id, icon: FaEnvelope, copy: true },
        { label: "Phone", value: user.phone_num, icon: FaPhoneAlt, copy: true },
        { label: "Gender", value: user.gender },
        { label: "DOB", value: nm(user.dob) ? `${fmtDate(parseAny(user.dob))}${ageOf(user.dob) ? ` (${ageOf(user.dob)} yrs)` : ""}` : null },
        { label: "Language", value: user.language },
        { label: "Profession", value: user.profession },
        { label: "Study", value: user.study },
        { label: "Referral Code", value: user.reffral_code, copy: true },
        { label: "Referred by", value: user.reffer_by, copy: true },
      ]
    : [];
  const address = user
    ? [
        { label: "Country", value: user.country },
        { label: "State", value: user.state },
        { label: "District", value: user.district },
        { label: "Village", value: user.village },
        { label: "Address", value: user.address },
      ]
    : [];
  const allFields = [...personal, ...address];
  const filled = allFields.filter((f) => nm(f.value)).length;
  const completeness = allFields.length ? Math.round((filled / allFields.length) * 100) : 0;

  const status = user?.account_status || d.me?.status || "";
  const blocked = String(status).toLowerCase() === "block";
  const regDate = parseAny(`${user?.reg_date || ""} ${user?.reg_time || ""}`.trim()) || parseAny(user?.reg_date);
  const daysMember = regDate ? Math.max(0, Math.floor((Date.now() - regDate.getTime()) / 86400000)) : null;

  const walletBal = d.walletData?.wallet?.balance;
  const txns = d.walletData?.transactions || [];
  const earned = txns.filter((t) => t.tnx_type === "credit").reduce((s, t) => s + num(t.amount), 0);
  const adsBal = d.adsData?.wallet?.wallet_balance;
  const adsHold = d.adsData?.wallet?.hold_balance;
  const totalViews = d.videos.reduce((s, v) => s + num(v.views_num), 0);
  const totalPointsUsed = d.videos.reduce((s, v) => s + num(v.point_used), 0);
  const flags = d.reportsAgainst.length + d.msgReportsAgainst.length;

  /* ----- actions ----- */

  const copyText = (t, label = "Copied") => {
    navigator.clipboard?.writeText(String(t));
    toast.current?.show({ severity: "info", summary: label, detail: String(t), life: 1500 });
  };

  const runConfirm = async () => {
    if (!confirm) return;
    setWorking(true);
    try {
      let res;
      if (confirm === "block" || confirm === "unblock" || confirm === "delete") {
        res = await post("users.php", "block-user", { user_id, status: confirm === "block" ? "Block" : confirm === "unblock" ? "Live" : "Delete" });
        if (res?.status === false) throw new Error("Failed");
      } else {
        res = await post("users.php", confirm === "makeAdmin" ? "add-admin" : "remove-admin", { user_id });
        if (res?.status === false) throw new Error(res?.error || "Failed");
      }
      const msg = { block: "User blocked", unblock: "User unblocked", delete: "User deleted", makeAdmin: "User is now an admin", removeAdmin: "Admin role removed" }[confirm];
      toast.current?.show({ severity: "success", summary: "Done", detail: msg });
      setConfirm(null);
      setDeleteText("");
      if (confirm === "delete") {
        setTimeout(() => navigate(-1), 800);
      } else {
        fetchUser();
        fetchExtra();
      }
    } catch (e) {
      toast.current?.show({ severity: "error", summary: "Error", detail: e.message });
    }
    setWorking(false);
  };

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && !working && setConfirm(null);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [working]);

  /* ================= render ================= */

  const wrap = (children) => (
    <div style={currentMode === "Dark" ? { backgroundColor: "#20232A" } : {}} className="w-full p-4 sm:p-6 lg:p-8 bg-slate-50 min-h-screen">
      <style>{CSS}</style>
      <Toast ref={toast} />
      {children}
    </div>
  );

  if (loading && !user) {
    return wrap(
      <div className="space-y-4">
        <div className="h-56 rounded-3xl bg-slate-200 dark:bg-slate-700 animate-pulse" />
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="h-24 rounded-2xl bg-slate-200 dark:bg-slate-700 animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  if (!user || user.notFound || user.error) {
    return wrap(
      <div className={`${CARD} max-w-lg mx-auto mt-10 p-8 text-center`}>
        <FaUserSlash className="mx-auto text-slate-300" size={44} />
        <h1 className="mt-4 text-xl font-bold text-slate-900 dark:text-white">{user?.error ? "Could not load this profile" : "This user does not exist"}</h1>
        <p className="text-sm text-slate-500 mt-1">{user?.error ? "Check your connection and try again." : `User ID ${user_id} was deleted or never existed. Old records may still point to it.`}</p>
        <div className="mt-5 flex justify-center gap-2">
          <button type="button" onClick={() => navigate(-1)} className="up-press h-10 px-4 rounded-xl border border-slate-200 text-sm font-semibold text-slate-700 inline-flex items-center gap-2">
            <FaArrowLeft size={11} /> Go back
          </button>
          {user?.error && (
            <button type="button" onClick={fetchUser} className="up-press h-10 px-4 rounded-xl bg-slate-900 text-white text-sm font-semibold">
              Try again
            </button>
          )}
        </div>
      </div>
    );
  }

  const name = nm(user.name) || "No name";
  const initials = name
    .split(/\s+/)
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
  const hasPhoto = user.profile_img && !/dummy_profile/i.test(user.profile_img) && !imgErr;

  const TILES = [
    { id: "overview", label: "Points", value: d.me ? num(d.me.point).toLocaleString("en-IN") : "–", icon: FaCoins, tint: "from-amber-400 to-orange-500" },
    { id: "money", label: "Wallet", value: walletBal !== undefined ? money(walletBal) : "₹0", icon: FaWallet, tint: "from-emerald-500 to-teal-600", sub: d.wdPending.length ? `${money(d.wdPending.reduce((s, w) => s + num(w.amount), 0))} withdraw pending` : null },
    { id: "money", label: "Ads rewards", value: adsBal !== undefined ? money(adsBal) : "₹0", icon: FaAd, tint: "from-indigo-500 to-violet-600", sub: num(adsHold) ? `${money(adsHold)} on hold` : null },
    { id: "groups", label: "Groups", value: d.memberships.length, icon: FaUsers, tint: "from-cyan-500 to-blue-600", sub: d.referred.length ? `${d.referred.length} referred` : null },
    { id: "videos", label: "Videos", value: d.videos.length, icon: FaVideo, tint: "from-rose-500 to-red-600", sub: totalViews ? `${totalViews.toLocaleString("en-IN")} views` : null },
    { id: "reports", label: "Reports", value: flags, icon: FaFlag, tint: flags ? "from-rose-600 to-pink-700" : "from-slate-400 to-slate-500", sub: flags ? "against this user" : "clean record" },
  ];

  const TABS = [
    { id: "overview", label: "Details" },
    { id: "money", label: "Money", count: txns.length },
    { id: "groups", label: "Groups & network", count: d.memberships.length + d.customers.length },
    { id: "videos", label: "Videos", count: d.videos.length },
    { id: "reports", label: "Reports", count: flags + d.reportsBy.length },
    { id: "system", label: "System" },
  ];

  const Field = ({ f }) => {
    const v = nm(f.value);
    const Icon = f.icon;
    return (
      <div className="rounded-xl bg-slate-50 dark:bg-slate-800/60 px-3 py-2.5">
        <p className="text-[11px] text-slate-500 inline-flex items-center gap-1.5">
          {Icon && <Icon size={9} />} {f.label}
        </p>
        <div className="flex items-center gap-2">
          <p className={`text-sm font-semibold break-all ${v ? "text-slate-900 dark:text-white" : "text-slate-300 dark:text-slate-600 italic font-normal"}`}>{v || "Not added"}</p>
          {v && f.copy && (
            <button type="button" onClick={() => copyText(v, `${f.label} copied`)} className="text-slate-400 hover:text-slate-700 shrink-0" aria-label={`Copy ${f.label}`}>
              <FaCopy size={10} />
            </button>
          )}
        </div>
      </div>
    );
  };

  const shownVideos = videoType === "all" ? d.videos : d.videos.filter((v) => v.link_type === videoType);

  return wrap(
    <>
      {/* ================= Hero ================= */}
      <div className="up-in relative overflow-hidden rounded-3xl bg-white dark:bg-secondary-dark-bg border border-slate-200 dark:border-slate-700">
        <div className={`h-32 sm:h-36 bg-gradient-to-r ${blocked ? "from-rose-600 via-rose-700 to-slate-900" : "from-cyan-600 via-teal-600 to-slate-900"} relative`}>
          <div className="absolute inset-0 opacity-20" style={{ backgroundImage: "radial-gradient(circle at 20% 30%, white 1px, transparent 1px)", backgroundSize: "18px 18px" }} />
          <button type="button" onClick={() => navigate(-1)} className="up-press absolute left-4 top-4 inline-flex items-center gap-2 h-9 px-3 rounded-xl bg-white/15 hover:bg-white/25 text-white text-sm font-semibold backdrop-blur">
            <FaArrowLeft size={11} /> Back
          </button>
          <button type="button" onClick={() => { fetchUser(); fetchExtra(); }} className="up-press absolute right-4 top-4 inline-flex items-center gap-2 h-9 px-3 rounded-xl bg-white/15 hover:bg-white/25 text-white text-sm font-semibold backdrop-blur">
            <FaSyncAlt size={11} className={extraLoading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>

        <div className="px-5 sm:px-7 pb-6">
          <div className="flex flex-col xl:flex-row gap-4 xl:gap-6">
            <div className="relative shrink-0 -mt-14 self-start">
              {hasPhoto ? (
                <img src={user.profile_img} alt={name} onError={() => setImgErr(true)} className="w-28 h-28 rounded-3xl object-cover ring-4 ring-white dark:ring-slate-800 shadow-xl bg-slate-100" />
              ) : (
                <span className="w-28 h-28 rounded-3xl ring-4 ring-white dark:ring-slate-800 shadow-xl bg-gradient-to-br from-teal-500 to-cyan-700 text-white text-4xl font-black flex items-center justify-center">{initials}</span>
              )}
              <span className={`absolute -bottom-1 -right-1 w-6 h-6 rounded-full ring-4 ring-white dark:ring-slate-800 ${blocked ? "bg-rose-500" : "bg-emerald-500"}`} title={status} />
            </div>

            <div className="flex-1 min-w-0 xl:pt-4">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="text-2xl sm:text-3xl font-black tracking-tight text-slate-900 dark:text-white break-words">{name}</h1>
                <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${blocked ? "bg-rose-100 text-rose-700" : "bg-emerald-100 text-emerald-700"}`}>{blocked ? "Blocked" : status || "Live"}</span>
                {d.isAdmin && (
                  <span className="inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-full bg-slate-900 text-white dark:bg-white dark:text-slate-900">
                    <FaCrown size={10} className="text-amber-400" /> Admin
                  </span>
                )}
                {d.adminReq && !d.isAdmin && <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-amber-100 text-amber-800">Admin request pending</span>}
              </div>
              <p className="text-sm text-slate-500 mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
                {nm(user.profession) && <span>{user.profession}</span>}
                <button type="button" onClick={() => copyText(user.user_id, "User ID copied")} className="inline-flex items-center gap-1 font-mono text-xs hover:text-slate-800">
                  ID {user.user_id} <FaCopy size={9} />
                </button>
                {regDate && (
                  <span className="inline-flex items-center gap-1">
                    <FaCalendarAlt size={10} /> Member since {fmtDate(regDate)} ({daysMember} days)
                  </span>
                )}
                {[user.district, user.state].filter(nm).length > 0 && (
                  <span className="inline-flex items-center gap-1">
                    <FaMapMarkerAlt size={10} /> {[user.district, user.state].filter(nm).join(", ")}
                  </span>
                )}
              </p>
              <div className="mt-2 flex flex-wrap gap-2">
                {nm(user.phone_num) && (
                  <button type="button" onClick={() => copyText(user.phone_num, "Phone copied")} className="up-press inline-flex items-center gap-1.5 h-7 px-2.5 rounded-lg bg-slate-100 dark:bg-slate-700 text-xs font-semibold text-slate-700 dark:text-slate-200 tabular-nums">
                    <FaPhoneAlt size={9} /> {user.phone_num} <FaCopy size={8} className="opacity-50" />
                  </button>
                )}
                {nm(user.email_id) && (
                  <button type="button" onClick={() => copyText(user.email_id, "Email copied")} className="up-press inline-flex items-center gap-1.5 h-7 px-2.5 rounded-lg bg-slate-100 dark:bg-slate-700 text-xs font-semibold text-slate-700 dark:text-slate-200 max-w-full truncate">
                    <FaEnvelope size={9} /> {user.email_id} <FaCopy size={8} className="opacity-50" />
                  </button>
                )}
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2 xl:pt-4 xl:self-start xl:justify-end xl:max-w-[520px]">
              {nm(user.phone_num) && (
                <a href={`tel:${user.phone_num}`} className="up-press w-10 h-10 rounded-xl border border-slate-200 dark:border-slate-600 flex items-center justify-center text-slate-600 dark:text-slate-300" aria-label="Call" title="Call">
                  <FaPhoneAlt size={13} />
                </a>
              )}
              {waLink(user.phone_num) && (
                <a href={waLink(user.phone_num)} target="_blank" rel="noreferrer" className="up-press w-10 h-10 rounded-xl bg-emerald-50 text-emerald-700 flex items-center justify-center" aria-label="WhatsApp" title="WhatsApp">
                  <FaWhatsapp size={16} />
                </a>
              )}
              {nm(user.email_id) && (
                <a href={`mailto:${user.email_id}`} className="up-press w-10 h-10 rounded-xl border border-slate-200 dark:border-slate-600 flex items-center justify-center text-slate-600 dark:text-slate-300" aria-label="Email" title="Email">
                  <FaEnvelope size={13} />
                </a>
              )}
              <button type="button" onClick={() => setConfirm(d.isAdmin ? "removeAdmin" : "makeAdmin")} className="up-press h-10 px-3.5 rounded-xl border border-slate-200 dark:border-slate-600 text-sm font-semibold text-slate-700 dark:text-slate-200 inline-flex items-center gap-2">
                {d.isAdmin ? <FaUserMinus size={12} /> : <FaUserTie size={12} />} {d.isAdmin ? "Remove admin" : "Make admin"}
              </button>
              <button type="button" onClick={() => setConfirm(blocked ? "unblock" : "block")} className={`up-press h-10 px-3.5 rounded-xl text-sm font-semibold inline-flex items-center gap-2 ${blocked ? "bg-emerald-600 hover:bg-emerald-700 text-white" : "bg-amber-500 hover:bg-amber-600 text-white"}`}>
                {blocked ? <FaUnlock size={12} /> : <FaBan size={12} />} {blocked ? "Unblock" : "Block"}
              </button>
              <button type="button" onClick={() => { setDeleteText(""); setConfirm("delete"); }} className="up-press h-10 px-3.5 rounded-xl bg-rose-600 hover:bg-rose-700 text-white text-sm font-semibold inline-flex items-center gap-2">
                <FaTrash size={11} /> Delete
              </button>
            </div>
          </div>

          {/* Profile completeness */}
          <div className="mt-5 flex items-center gap-3">
            <div className="flex-1 h-2 rounded-full bg-slate-100 dark:bg-slate-700 overflow-hidden">
              <div className={`up-bar h-full rounded-full ${completeness >= 80 ? "bg-emerald-500" : completeness >= 50 ? "bg-amber-500" : "bg-rose-500"}`} style={{ width: `${completeness}%` }} />
            </div>
            <span className="text-xs font-semibold text-slate-600 dark:text-slate-300 whitespace-nowrap">Profile {completeness}% filled</span>
          </div>
        </div>
      </div>

      {/* ================= Tiles ================= */}
      <div className="mt-4 grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        {TILES.map((t, i) => {
          const Icon = t.icon;
          return (
            <button key={t.label} type="button" onClick={() => setTab(t.id)} className={`up-tile up-in ${CARD} p-4 text-left`} style={{ animationDelay: `${i * 50}ms` }}>
              <span className={`w-9 h-9 rounded-xl bg-gradient-to-br ${t.tint} text-white flex items-center justify-center`}>
                <Icon size={14} />
              </span>
              <p className="mt-2 text-xl font-black tabular-nums text-slate-900 dark:text-white">{extraLoading && t.id !== "overview" && !extra.wallet ? "…" : t.value}</p>
              <p className="text-xs text-slate-500">{t.label}</p>
              {t.sub && <p className="text-[11px] text-slate-500 truncate">{t.sub}</p>}
            </button>
          );
        })}
      </div>

      {/* ================= Tabs ================= */}
      <div className="mt-5 flex gap-1 overflow-x-auto border-b border-slate-200 dark:border-slate-700">
        {TABS.map((t) => (
          <button key={t.id} type="button" onClick={() => setTab(t.id)} className={`relative shrink-0 px-4 py-2.5 text-sm font-semibold transition-colors ${tab === t.id ? "text-teal-700 dark:text-teal-300" : "text-slate-500 hover:text-slate-800 dark:hover:text-slate-200"}`}>
            {t.label}
            {t.count > 0 && <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 tabular-nums">{t.count}</span>}
            {tab === t.id && <span className="absolute inset-x-2 -bottom-px h-0.5 rounded-full bg-teal-600" />}
          </button>
        ))}
      </div>

      <div key={tab} className="up-fade mt-4">
        {/* Details */}
        {tab === "overview" && (
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
            <section className={`${CARD} p-5 xl:col-span-2`}>
              <h2 className="text-sm font-semibold text-slate-900 dark:text-white mb-3 inline-flex items-center gap-2">
                <FaIdCard className="text-teal-600" /> Personal Information
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {personal.map((f) => (
                  <Field key={f.label} f={f} />
                ))}
              </div>
              <h2 className="text-sm font-semibold text-slate-900 dark:text-white mt-5 mb-3 inline-flex items-center gap-2">
                <FaMapMarkerAlt className="text-teal-600" /> Address Information
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {address.map((f) => (
                  <Field key={f.label} f={f} />
                ))}
              </div>
            </section>

            <section className="space-y-4">
              <div className={`${CARD} p-5`}>
                <h2 className="text-sm font-semibold text-slate-900 dark:text-white mb-3">At a glance</h2>
                <ul className="space-y-2.5 text-sm">
                  {[
                    { label: "Account", value: blocked ? "Blocked" : "Active", ok: !blocked },
                    { label: "Admin", value: d.isAdmin ? "Yes" : d.adminReq ? "Request pending" : "No", ok: true },
                    { label: "Group memberships", value: d.memberships.length, ok: true },
                    { label: "People referred to groups", value: d.referred.length, ok: true },
                    { label: "Customers brought", value: d.customers.length, ok: true },
                    { label: "Total earned (wallet)", value: money(earned), ok: true },
                    { label: "Reports against", value: flags, ok: flags === 0 },
                  ].map((r) => (
                    <li key={r.label} className="flex items-center justify-between gap-2">
                      <span className="text-slate-500">{r.label}</span>
                      <b className={`tabular-nums ${r.ok ? "text-slate-900 dark:text-white" : "text-rose-600"}`}>{r.value}</b>
                    </li>
                  ))}
                </ul>
              </div>
              {completeness < 100 && (
                <div className="rounded-2xl border border-amber-200 dark:border-amber-900 bg-amber-50 dark:bg-amber-900/20 p-4">
                  <p className="text-sm font-semibold text-amber-900 dark:text-amber-200">Missing details</p>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {allFields
                      .filter((f) => !nm(f.value))
                      .map((f) => (
                        <span key={f.label} className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-white/70 dark:bg-slate-800 text-amber-800 dark:text-amber-200">
                          {f.label}
                        </span>
                      ))}
                  </div>
                </div>
              )}
            </section>
          </div>
        )}

        {/* Money */}
        {tab === "money" && (
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
            <section className="space-y-4">
              <div className="rounded-2xl bg-gradient-to-br from-emerald-600 to-teal-800 text-white p-5">
                <p className="text-xs text-emerald-100 inline-flex items-center gap-1.5">
                  <FaWallet size={10} /> Group wallet
                </p>
                <p className="text-3xl font-black tabular-nums">{money(walletBal)}</p>
                <p className="text-[11px] text-emerald-100/80">Earned in total {money(earned)}</p>
                <button type="button" onClick={() => navigate(`/wallet-account/${user_id}`)} className="up-press mt-3 inline-flex items-center gap-1.5 h-8 px-3 rounded-lg bg-white/15 text-xs font-semibold">
                  Full wallet <FaExternalLinkAlt size={9} />
                </button>
              </div>
              <div className="rounded-2xl bg-gradient-to-br from-indigo-600 to-violet-800 text-white p-5">
                <p className="text-xs text-indigo-100 inline-flex items-center gap-1.5">
                  <FaAd size={10} /> Ads rewards
                </p>
                <p className="text-3xl font-black tabular-nums">{money(adsBal)}</p>
                <p className="text-[11px] text-indigo-100/80">{money(adsHold)} on hold</p>
                <button type="button" onClick={() => navigate(`/ads-reward-wallet-account/${user_id}`)} className="up-press mt-3 inline-flex items-center gap-1.5 h-8 px-3 rounded-lg bg-white/15 text-xs font-semibold">
                  Full ads wallet <FaExternalLinkAlt size={9} />
                </button>
              </div>
              <div className={`${CARD} p-4`}>
                <p className="text-sm font-semibold text-slate-900 dark:text-white">Withdrawals</p>
                <div className="mt-2 grid grid-cols-2 gap-2 text-center">
                  <div className="rounded-xl bg-amber-50 dark:bg-amber-900/20 p-2.5">
                    <p className="text-[10px] text-amber-700">Waiting</p>
                    <p className="font-bold tabular-nums text-amber-700">{money(d.wdPending.reduce((s, w) => s + num(w.amount), 0))}</p>
                  </div>
                  <div className="rounded-xl bg-emerald-50 dark:bg-emerald-900/20 p-2.5">
                    <p className="text-[10px] text-emerald-700">Paid out</p>
                    <p className="font-bold tabular-nums text-emerald-700">{money(d.wdPaid.reduce((s, w) => s + num(w.amount), 0))}</p>
                  </div>
                </div>
                {d.wdPending.length > 0 && (
                  <button type="button" onClick={() => navigate("/wallet/withdrawal-req")} className="mt-2 text-xs font-semibold text-amber-700 hover:underline">
                    Review pending withdrawal →
                  </button>
                )}
              </div>
            </section>
            <section className={`${CARD} p-5 xl:col-span-2`}>
              <h2 className="text-sm font-semibold text-slate-900 dark:text-white mb-3">Wallet transactions</h2>
              {!txns.length ? (
                <p className="text-sm text-slate-500">{extraLoading ? "Loading..." : "No transactions yet"}</p>
              ) : (
                <ul className="space-y-2 max-h-[520px] overflow-y-auto pr-1">
                  {txns.slice(0, 60).map((t) => {
                    const cr = t.tnx_type === "credit";
                    const td = parseAny(t.date_time);
                    return (
                      <li key={t.tnx_id} className="flex items-center gap-3 rounded-xl bg-slate-50 dark:bg-slate-800/60 px-3 py-2.5">
                        <span className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${cr ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`}>{cr ? <FaArrowDown size={11} /> : <FaArrowUp size={11} />}</span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-slate-800 dark:text-slate-100 truncate" title={t.remark}>
                            {t.remark || t.tnx_type}
                          </p>
                          <p className="text-[11px] text-slate-500">
                            {fmtDate(td)}, {fmtTime(td)}
                          </p>
                        </div>
                        <b className={`tabular-nums ${cr ? "text-emerald-700" : "text-rose-700"}`}>
                          {cr ? "+" : "-"}
                          {money(t.amount)}
                        </b>
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>
          </div>
        )}

        {/* Groups & network */}
        {tab === "groups" && (
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            <section className={`${CARD} p-5`}>
              <h2 className="text-sm font-semibold text-slate-900 dark:text-white mb-3 inline-flex items-center gap-2">
                <FaUsers className="text-cyan-600" /> Group memberships
              </h2>
              {!d.memberships.length ? (
                <p className="text-sm text-slate-500">Not in any group</p>
              ) : (
                <ul className="space-y-2">
                  {d.memberships.map((m) => (
                    <li key={m.index_id} className="flex items-center gap-3 rounded-xl border border-slate-200 dark:border-slate-700 px-3 py-2.5">
                      <span className={`w-8 h-8 rounded-lg flex items-center justify-center ${String(m.platform).toLowerCase() === "facebook" ? "bg-blue-100 text-blue-700" : "bg-red-100 text-red-700"}`}>{String(m.platform).toLowerCase() === "facebook" ? <FaFacebookSquare /> : <FaYoutube />}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-slate-900 dark:text-white truncate inline-flex items-center gap-1.5">
                          {Number(m.group_id) <= 2 && <FaCrown size={10} className="text-amber-500" />} {m.grp_name}
                          {String(m.role).toLowerCase() === "admin" && <span className="text-[9px] font-bold px-1 rounded bg-slate-900 text-white">ADMIN</span>}
                        </p>
                        <p className="text-[11px] text-slate-500">
                          {m.pkg_name || "No package"} • paid {money(m.paid_amount)} • since {fmtDate(parseAny(m.date_time))}
                        </p>
                      </div>
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${String(m.status).toLowerCase() === "block" ? "bg-rose-100 text-rose-700" : "bg-emerald-100 text-emerald-700"}`}>{String(m.status).toLowerCase() === "block" ? "BLOCKED" : "ACTIVE"}</span>
                    </li>
                  ))}
                </ul>
              )}
              {d.joinReq.length > 0 && (
                <button type="button" onClick={() => navigate("/group/join-request")} className="mt-3 w-full flex items-center gap-2 rounded-xl bg-amber-50 dark:bg-amber-900/20 px-3 py-2.5 text-sm text-amber-900 dark:text-amber-200 text-left">
                  <FaHourglassHalf size={12} /> {d.joinReq.length} joining request{d.joinReq.length > 1 ? "s" : ""} waiting ({money(d.joinReq.reduce((s, j) => s + num(j.paid_amount), 0))})
                </button>
              )}
              {d.ownGroups.length > 0 && <p className="mt-3 text-xs text-slate-500">Created {d.ownGroups.length} sub group{d.ownGroups.length > 1 ? "s" : ""}.</p>}
            </section>

            <section className="space-y-4">
              <div className={`${CARD} p-5`}>
                <h2 className="text-sm font-semibold text-slate-900 dark:text-white mb-3 inline-flex items-center gap-2">
                  <FaUserFriends className="text-violet-600" /> People they brought into groups ({d.referred.length})
                </h2>
                {!d.referred.length ? (
                  <p className="text-sm text-slate-500">No referrals yet</p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {d.referred.slice(0, 40).map((m) => (
                      <button key={m.member_id} type="button" onClick={() => navigate(`/user-profile/${m.member_id}`)} className="up-press inline-flex items-center gap-1.5 h-8 px-3 rounded-full border border-slate-200 dark:border-slate-600 text-xs font-semibold text-slate-700 dark:text-slate-200 hover:border-violet-400">
                        {nm(m.member_name) || m.member_id}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className={`${CARD} p-5`}>
                <h2 className="text-sm font-semibold text-slate-900 dark:text-white mb-3 inline-flex items-center gap-2">
                  <FaUserCheck className="text-teal-600" /> Customers they brought ({d.customers.length})
                </h2>
                {!d.customers.length ? (
                  <p className="text-sm text-slate-500">No customers yet</p>
                ) : (
                  <ul className="space-y-1.5">
                    {d.customers.slice(0, 20).map((c) => (
                      <li key={c.cust_id} className="flex items-center justify-between gap-2 text-sm">
                        <span className="truncate text-slate-800 dark:text-slate-100">
                          {c.name} <span className="text-xs text-slate-500">• {c.pkg_name}</span>
                        </span>
                        <b className="tabular-nums text-amber-700">{money((num(c.pkg_price) * num(c.comission)) / 100)}</b>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </section>
          </div>
        )}

        {/* Videos */}
        {tab === "videos" && (
          <section className={`${CARD} p-5`}>
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <div className="flex flex-wrap gap-1.5">
                {[{ type: "all" }, ...VIDEO_TYPES].map((v) => {
                  const Icon = v.icon;
                  const count = v.type === "all" ? d.videos.length : d.videos.filter((x) => x.link_type === v.type).length;
                  return (
                    <button key={v.type} type="button" onClick={() => setVideoType(v.type)} className={`up-press inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-xs font-semibold border ${videoType === v.type ? "bg-slate-900 text-white border-slate-900 dark:bg-white dark:text-slate-900" : "border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-300"}`}>
                      {Icon && <Icon className={videoType === v.type ? "" : v.cls} size={11} />} {v.type === "all" ? "All" : v.type} <span className="opacity-60">{count}</span>
                    </button>
                  );
                })}
              </div>
              <p className="text-xs text-slate-500">
                {totalViews.toLocaleString("en-IN")} views • {totalPointsUsed.toLocaleString("en-IN")} points used
              </p>
            </div>
            {extraLoading && !d.videos.length ? (
              <p className="text-sm text-slate-500">Loading videos...</p>
            ) : !shownVideos.length ? (
              <p className="text-sm text-slate-500">No videos</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-3">
                {shownVideos.map((v) => {
                  const id = ytId(v.link_url);
                  const vd = parseAny(v.link_date);
                  return (
                    <a key={v.post_id} href={v.link_url} target="_blank" rel="noreferrer" className="up-tile group rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden bg-white dark:bg-slate-800">
                      <div className="aspect-video bg-slate-900 relative overflow-hidden">
                        {id ? <img src={`https://img.youtube.com/vi/${id}/mqdefault.jpg`} alt="" className="w-full h-full object-cover" loading="lazy" /> : <span className="absolute inset-0 flex items-center justify-center text-white/60 text-xs">{v.link_type}</span>}
                        <span className="absolute left-2 top-2 text-[10px] font-bold px-1.5 py-0.5 rounded bg-black/60 text-white">{v.link_type}</span>
                      </div>
                      <div className="p-3">
                        <div className="flex items-center justify-between text-xs">
                          <span className="inline-flex items-center gap-1 text-slate-600 dark:text-slate-300">
                            <FaEye size={10} /> {num(v.views_num)} views
                          </span>
                          <span className="inline-flex items-center gap-1 text-amber-700">
                            <FaCoins size={10} /> {num(v.point_used)}
                          </span>
                        </div>
                        <p className="text-[11px] text-slate-500 mt-1">
                          {fmtDate(vd)} • {num(v.watch_time)}s watch
                        </p>
                      </div>
                    </a>
                  );
                })}
              </div>
            )}
          </section>
        )}

        {/* Reports */}
        {tab === "reports" && (
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            <section className={`${CARD} p-5`}>
              <h2 className="text-sm font-semibold text-slate-900 dark:text-white mb-3 inline-flex items-center gap-2">
                <FaFlag className="text-rose-600" /> Reports against this user ({flags})
              </h2>
              {!flags ? (
                <p className="text-sm text-emerald-700 inline-flex items-center gap-2">
                  <FaCheckCircle /> Clean record
                </p>
              ) : (
                <ul className="space-y-2">
                  {d.reportsAgainst.map((r) => (
                    <li key={`u${r.report_id}`} className="rounded-xl border border-rose-200 dark:border-rose-900 bg-rose-50/50 dark:bg-rose-900/10 px-3 py-2.5">
                      <p className="text-sm font-semibold text-slate-900 dark:text-white">{r.title || "Profile report"}</p>
                      <p className="text-xs text-slate-600 dark:text-slate-300 line-clamp-2">{r.reason}</p>
                      <p className="text-[11px] text-slate-500 mt-1">
                        by {nm(r.reporter_name) || r.report_by} • {fmtDate(parseAny(r.date_time))}
                      </p>
                    </li>
                  ))}
                  {d.msgReportsAgainst.map((r) => (
                    <li key={`m${r.report_id}-${r.msg_id}`} className="rounded-xl border border-rose-200 dark:border-rose-900 bg-rose-50/50 dark:bg-rose-900/10 px-3 py-2.5">
                      <p className="text-sm font-semibold text-slate-900 dark:text-white">{r.title || "Message report"}</p>
                      <p className="text-xs text-slate-600 dark:text-slate-300 line-clamp-2">"{r.message_text || "media message"}"</p>
                      <p className="text-[11px] text-slate-500 mt-1">
                        {r.grp_name ? `in ${r.grp_name}` : "private chat"} • by {nm(r.reporter_name) || r.reporter_id} • {fmtDate(parseAny(r.date_time))}
                      </p>
                    </li>
                  ))}
                </ul>
              )}
              {flags > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  <button type="button" onClick={() => navigate("/report")} className="up-press h-8 px-3 rounded-lg border border-slate-200 dark:border-slate-600 text-xs font-semibold text-slate-700 dark:text-slate-200">
                    Open Report Request
                  </button>
                  {!blocked && (
                    <button type="button" onClick={() => setConfirm("block")} className="up-press h-8 px-3 rounded-lg bg-amber-500 text-white text-xs font-semibold inline-flex items-center gap-1.5">
                      <FaBan size={10} /> Block this user
                    </button>
                  )}
                </div>
              )}
            </section>
            <section className={`${CARD} p-5`}>
              <h2 className="text-sm font-semibold text-slate-900 dark:text-white mb-3">Reports this user made ({d.reportsBy.length})</h2>
              {!d.reportsBy.length ? (
                <p className="text-sm text-slate-500">None</p>
              ) : (
                <ul className="space-y-2">
                  {d.reportsBy.map((r) => (
                    <li key={r.report_id} className="rounded-xl bg-slate-50 dark:bg-slate-800/60 px-3 py-2.5">
                      <p className="text-sm font-semibold text-slate-900 dark:text-white">{r.title || "Report"}</p>
                      <p className="text-[11px] text-slate-500">
                        against {nm(r.reported_user_name) || r.user_id} • {fmtDate(parseAny(r.date_time))}
                      </p>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>
        )}

        {/* System */}
        {tab === "system" && (
          <section className={`${CARD} p-5`}>
            <h2 className="text-sm font-semibold text-slate-900 dark:text-white mb-3 inline-flex items-center gap-2">
              <FaMobileAlt className="text-slate-500" /> System Info
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
              {[
                { label: "User ID", value: user.user_id, copy: true },
                { label: "Device ID", value: user.device_id, copy: true },
                { label: "Reg. Date", value: user.reg_date },
                { label: "Reg. Time", value: user.reg_time },
                { label: "Admin ID", value: user.admin_id },
                { label: "Account Status", value: status },
              ].map((f) => (
                <Field key={f.label} f={f} />
              ))}
            </div>
            <p className="mt-3 text-[11px] text-slate-500">The Device ID is used to send notifications to this user's phone.</p>
          </section>
        )}
      </div>

      {/* ================= Confirm ================= */}
      {confirm && (
        <div className="fixed inset-0 flex items-center justify-center p-4" style={{ zIndex: 1200 }}>
          <div className="up-fade absolute inset-0 bg-slate-900/60" onClick={() => !working && setConfirm(null)} />
          <div className="up-zoom relative w-full max-w-md rounded-2xl bg-white dark:bg-secondary-dark-bg shadow-2xl p-5">
            {(() => {
              const c = {
                block: { title: `Block ${name}?`, body: "They cannot use the app and get an 'Account Suspended' notification.", btn: "Yes, block", cls: "bg-amber-500 hover:bg-amber-600" },
                unblock: { title: `Unblock ${name}?`, body: "They can use the app again and get an 'Account Activated' notification.", btn: "Yes, unblock", cls: "bg-emerald-600 hover:bg-emerald-700" },
                delete: { title: `Delete ${name} forever?`, body: `Everything is removed: profile, points, wallet (${money(walletBal)}), group memberships, posts, withdrawals and reports. This cannot be undone.`, btn: "Delete forever", cls: "bg-rose-600 hover:bg-rose-700" },
                makeAdmin: { title: `Make ${name} an admin?`, body: "They get admin powers in the app.", btn: "Yes, make admin", cls: "bg-slate-900 hover:bg-slate-800" },
                removeAdmin: { title: `Remove admin role from ${name}?`, body: "They become a normal user again.", btn: "Yes, remove", cls: "bg-rose-600 hover:bg-rose-700" },
              }[confirm];
              const needType = confirm === "delete";
              return (
                <>
                  <span className={`w-12 h-12 rounded-2xl flex items-center justify-center ${confirm === "unblock" ? "bg-emerald-100 text-emerald-600" : "bg-rose-100 text-rose-600"}`}>
                    <FaExclamationTriangle size={18} />
                  </span>
                  <h3 className="mt-3 text-lg font-semibold text-slate-900 dark:text-white">{c.title}</h3>
                  <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">{c.body}</p>
                  {needType && (
                    <div className="mt-4">
                      <label htmlFor="up-del" className="text-sm text-slate-700 dark:text-slate-200">
                        Type <b>DELETE</b> to confirm
                      </label>
                      <input id="up-del" value={deleteText} onChange={(e) => setDeleteText(e.target.value)} autoFocus placeholder="DELETE" className="w-full h-10 px-3 mt-1.5 rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-rose-400" />
                    </div>
                  )}
                  <div className="flex gap-2 mt-5">
                    <button type="button" onClick={() => setConfirm(null)} disabled={working} className="up-press flex-1 h-11 rounded-xl border border-slate-200 dark:border-slate-600 text-sm font-medium text-slate-700 dark:text-slate-200">
                      Cancel
                    </button>
                    <button type="button" onClick={runConfirm} disabled={working || (needType && deleteText.trim().toUpperCase() !== "DELETE")} className={`up-press flex-1 h-11 rounded-xl text-white text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-50 ${c.cls}`}>
                      {working && <FaSyncAlt className="animate-spin" size={12} />}
                      {working ? "Please wait..." : c.btn}
                    </button>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}
    </>
  );
};

export default UserProfile;